// Pure geometry helpers. No DOM, no store — everything takes plain arguments.

import type { Entity, Pan } from "../state/types.js";

// World (DXF coordinates) → screen (canvas pixels).
// Y is flipped because DXF has +Y up while canvas has +Y down.
export function worldToScreen(
  x: number,
  y: number,
  zoom: number,
  pan: Pan,
  height: number
): { sx: number; sy: number } {
  return {
    sx: x * zoom + pan.x,
    sy: -y * zoom + pan.y + height / 2,
  };
}

export function screenToWorld(
  sx: number,
  sy: number,
  zoom: number,
  pan: Pan,
  height: number
): { x: number; y: number } {
  return {
    x: (sx - pan.x) / zoom,
    y: -(sy - pan.y - height / 2) / zoom,
  };
}

// Shortest distance from a point to a line segment.
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(px - x1, py - y1);
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// True iff the two closed segments (a1-a2) and (b1-b2) properly intersect
// -- meaning they cross or touch anywhere along their finite length. Uses
// the classic orientation test: two segments intersect when each pair of
// endpoints straddles the other segment's line (or when a colinear endpoint
// falls on the other segment).
export function segmentsIntersect(
  ax1: number,
  ay1: number,
  ax2: number,
  ay2: number,
  bx1: number,
  by1: number,
  bx2: number,
  by2: number
): boolean {
  const o = (px: number, py: number, qx: number, qy: number, rx: number, ry: number): number =>
    (qy - py) * (rx - qx) - (qx - px) * (ry - qy);
  const onSeg = (px: number, py: number, qx: number, qy: number, rx: number, ry: number): boolean =>
    qx <= Math.max(px, rx) && qx >= Math.min(px, rx) &&
    qy <= Math.max(py, ry) && qy >= Math.min(py, ry);

  const o1 = o(ax1, ay1, ax2, ay2, bx1, by1);
  const o2 = o(ax1, ay1, ax2, ay2, bx2, by2);
  const o3 = o(bx1, by1, bx2, by2, ax1, ay1);
  const o4 = o(bx1, by1, bx2, by2, ax2, ay2);

  if (o1 * o2 < 0 && o3 * o4 < 0) return true;
  // Colinear touching endpoints.
  if (o1 === 0 && onSeg(ax1, ay1, bx1, by1, ax2, ay2)) return true;
  if (o2 === 0 && onSeg(ax1, ay1, bx2, by2, ax2, ay2)) return true;
  if (o3 === 0 && onSeg(bx1, by1, ax1, ay1, bx2, by2)) return true;
  if (o4 === 0 && onSeg(bx1, by1, ax2, ay2, bx2, by2)) return true;
  return false;
}

// Point-in-polygon via ray casting. Polygon is an array of [x, y] pairs.
export function pointInPolygon(
  px: number,
  py: number,
  polygon: ReadonlyArray<readonly [number, number]>
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// ---- Entity-level shape queries ----
// These operate on the `Entity` shape (raw data + flattened points) so they
// stay pure -- no store access. Used by ops/entities for "Select All Round
// Holes", "Select All Inner-Cutouts", and "Select All Similar Cutouts".

// True when the entity encloses an area (CIRCLE always; polylines with the
// DXF closed flag set). Open polylines and LINE/ARC are not contours.
export function isClosedContour(entity: Entity): boolean {
  if (entity.type === "CIRCLE") return true;
  if (entity.type === "LWPOLYLINE" || entity.type === "POLYLINE") {
    return ((entity.data.flags ?? 0) & 1) === 1;
  }
  return false;
}

// Signed polygon area (shoelace). Positive = CCW, negative = CW.
function signedArea(pts: ReadonlyArray<readonly [number, number]>): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j]![0] + pts[i]![0]) * (pts[j]![1] - pts[i]![1]);
  }
  return a / 2;
}

export function polygonArea(pts: ReadonlyArray<readonly [number, number]>): number {
  return Math.abs(signedArea(pts));
}

export interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function pointsBBox(pts: ReadonlyArray<readonly [number, number]>): BBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return { minX, minY, maxX, maxY };
}

// Best-fit circle test: returns a radius if the entity's contour points all
// lie within `tolFrac` of a common radius from the shape's bbox center,
// otherwise null. A raw CIRCLE always returns its stored radius. Catches
// SolidWorks/Fusion polyline-approximated circles too.
//
// Bbox center is used instead of the point centroid because extractPoints
// closes a polyline by repeating vertex 0 at the end -- so a 36-vertex
// circle becomes 37 points with pts[0] == pts[36], and a naive centroid
// would sit off the true center by r/N, inflating rms/rmean past tolerance.
// For any uniformly-sampled circle, bbox center == true center regardless
// of duplicates.
export function fitCircleRadius(entity: Entity, tolFrac = 0.01): number | null {
  if (entity.type === "CIRCLE") {
    return entity.data.radius ?? null;
  }
  if (!isClosedContour(entity)) return null;
  const pts = entity.points;
  if (pts.length < 6) return null;   // need enough samples to be circle-ish
  const bb = pointsBBox(pts);
  const cx = (bb.minX + bb.maxX) / 2;
  const cy = (bb.minY + bb.maxY) / 2;
  let rSum = 0;
  for (const [x, y] of pts) rSum += Math.hypot(x - cx, y - cy);
  const rMean = rSum / pts.length;
  if (rMean <= 0) return null;
  let rmsSq = 0;
  for (const [x, y] of pts) {
    const d = Math.hypot(x - cx, y - cy) - rMean;
    rmsSq += d * d;
  }
  const rms = Math.sqrt(rmsSq / pts.length);
  if (rms / rMean > tolFrac) return null;
  // Radius uniformity alone lets a rounded rectangle with small corner radii
  // sneak through: every sample point on the tiny corner arcs sits at nearly
  // the same distance from the bbox center as every vertex sits at the
  // rectangle's own corner, so RMS stays under 1% even though the shape is
  // clearly not a circle. Chord lengths give the real answer — a uniformly-
  // sampled circle has near-equal chord segments, while a rounded rectangle
  // has a few long straight-side chords and many tiny arc-interior chords.
  // A max/mean chord ratio comfortably above 1 is a rounded polygon, not a
  // circle.
  let chordMax = 0;
  let chordSum = 0;
  let chordCount = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const c = Math.hypot(x1 - x0, y1 - y0);
    if (c <= 0) continue;
    if (c > chordMax) chordMax = c;
    chordSum += c;
    chordCount++;
  }
  if (chordCount === 0) return null;
  const chordMean = chordSum / chordCount;
  // Threshold 2.0: a perfect circle sits at 1.0; asymmetric sampling of a
  // real circle stays under ~1.5; a rounded rectangle (straight side vs.
  // arc-segment chords) is well past 2 (typically 5–100×).
  if (chordMax / chordMean > 2.0) return null;
  // Regular polygons defeat both checks above — every vertex sits at the
  // same radius (RMS = 0) and every chord is equal (max/mean = 1) — but
  // visually read as octagons / squircles / bevel-cut squares, not
  // circles. Discriminate by chord length relative to radius: a circle
  // sampled at N points has chord = 2R sin(π/N), so chord/R < 0.5 requires
  // N > ~12 — a 12-gon still reads as polygon; smoother shapes read as
  // circles.
  if (chordMax / rMean > 0.5) return null;
  return rMean;
}

// Bbox-then-point-in-polygon containment: is polygon `inner`'s representative
// point (its centroid) inside polygon `outer`? Bbox test first for cheap
// rejection. Good enough for typical CNC nesting where cutouts don't overlap
// their parents' boundary.
export function polygonContains(
  outer: ReadonlyArray<readonly [number, number]>,
  outerBBox: BBox,
  inner: ReadonlyArray<readonly [number, number]>,
  innerBBox: BBox
): boolean {
  if (
    innerBBox.minX < outerBBox.minX ||
    innerBBox.maxX > outerBBox.maxX ||
    innerBBox.minY < outerBBox.minY ||
    innerBBox.maxY > outerBBox.maxY
  ) {
    return false;
  }
  let cx = 0, cy = 0;
  for (const [x, y] of inner) { cx += x; cy += y; }
  cx /= inner.length;
  cy /= inner.length;
  return pointInPolygon(cx, cy, outer);
}

// Even-odd containment tree over a set of closed contours. Returns a map from
// entity id to its immediate parent's id (or null for top-level contours).
// "Inner cutout" = any entity with a non-null parent.
//
// Algorithm: sort contours by descending area, then for each contour walk the
// larger contours (which are candidate parents) and pick the smallest one
// whose interior contains the child's centroid. Larger-first ordering means
// we naturally find the tightest enclosing parent.
export function computeContainmentTree(
  closed: ReadonlyArray<Entity>
): Map<number, number | null> {
  const result = new Map<number, number | null>();
  const items = closed.map((e) => {
    const bbox = pointsBBox(e.points);
    return { id: e.id, points: e.points, bbox, area: polygonArea(e.points) };
  });
  items.sort((a, b) => b.area - a.area);
  for (let i = 0; i < items.length; i++) {
    const child = items[i]!;
    let parentId: number | null = null;
    // Candidates are the larger contours already visited (indices 0..i-1).
    for (let j = i - 1; j >= 0; j--) {
      const cand = items[j]!;
      if (polygonContains(cand.points, cand.bbox, child.points, child.bbox)) {
        parentId = cand.id;
        break;                    // items is sorted largest-first, so scanning
                                  // backward finds the smallest enclosing parent
      }
    }
    result.set(child.id, parentId);
  }
  return result;
}

// Escape untrusted text for insertion as HTML text content.
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
